// Lightweight procedural particle system for combat FX.
//
// One Container per burst; each particle is a Graphics object that
// integrates position/velocity/alpha/scale on its own rAF, then auto-removes
// itself + the container when its lifetime ends. No emitter pool — bursts
// are short and cheap; GC handles the rest.
//
// Particle "kinds" map to the four combat moments we need:
//   spark   — additive yellow/orange radial burst (impact)
//   shard   — fast-moving rect chips (divine shield pop)
//   drip    — slow vertical streaks tinted to faction color (death)
//   ring    — concentric expanding ring (summon)

import { Container, Graphics } from 'pixi.js';

type ParticleKind = 'spark' | 'shard' | 'drip' | 'ring';

interface Particle {
  gfx: Graphics;
  vx: number;
  vy: number;
  ax: number;
  ay: number;
  life: number;
  age: number;
  startScale: number;
  endScale: number;
  startAlpha: number;
}

interface BurstOptions {
  parent: Container;
  x: number;
  y: number;
  kind: ParticleKind;
  color: number;
  count?: number;
  speed?: number;
  /** Lifetime in ms */
  life?: number;
}

const DEFAULT_COUNT: Record<ParticleKind, number> = {
  spark: 36,
  shard: 18,
  drip: 14,
  ring: 6,
};

const DEFAULT_SPEED: Record<ParticleKind, number> = {
  spark: 240,
  shard: 360,
  drip: 90,
  ring: 0,
};

const DEFAULT_LIFE: Record<ParticleKind, number> = {
  spark: 380,
  shard: 520,
  drip: 720,
  ring: 520,
};

/**
 * Emit a burst at (x, y) inside the given parent container. The burst
 * cleans itself up — no caller-side bookkeeping required.
 */
export function emitBurst({
  parent,
  x,
  y,
  kind,
  color,
  count = DEFAULT_COUNT[kind],
  speed = DEFAULT_SPEED[kind],
  life = DEFAULT_LIFE[kind],
}: BurstOptions): void {
  const container = new Container();
  container.x = x;
  container.y = y;
  // Sparks read better additive against asphalt background.
  if (kind === 'spark' || kind === 'ring') {
    container.blendMode = 'add';
  }
  parent.addChild(container);

  const particles: Particle[] = [];

  for (let i = 0; i < count; i++) {
    const gfx = new Graphics();

    let angle = 0;
    let particleSpeed = 0;
    let lifeJitter = life;
    let startScale = 1;
    let endScale = 0.2;
    let ax = 0;
    let ay = 0;
    let startAlpha = 1;

    switch (kind) {
      case 'spark': {
        angle = (i / count) * Math.PI * 2 + Math.random() * 0.4;
        particleSpeed = speed * (0.5 + Math.random() * 0.9);
        lifeJitter = life * (0.7 + Math.random() * 0.5);
        ay = 320; // gravity-ish pull so sparks drop
        startScale = 1 + Math.random() * 0.6;
        endScale = 0.05;
        // 4-point spark glyph
        const r = 5;
        gfx.poly([0, -r, r * 0.4, 0, 0, r, -r * 0.4, 0]);
        gfx.fill({ color });
        break;
      }
      case 'shard': {
        angle = Math.random() * Math.PI * 2;
        particleSpeed = speed * (0.6 + Math.random() * 0.8);
        lifeJitter = life * (0.7 + Math.random() * 0.6);
        startScale = 1;
        endScale = 0.4;
        ay = 600;
        const w = 4 + Math.random() * 4;
        const h = 2 + Math.random() * 3;
        gfx.rect(-w / 2, -h / 2, w, h);
        gfx.fill({ color, alpha: 0.9 });
        gfx.rotation = Math.random() * Math.PI;
        break;
      }
      case 'drip': {
        angle = Math.PI / 2 + (Math.random() - 0.5) * 0.7; // mostly downward
        particleSpeed = speed * (0.4 + Math.random() * 0.8);
        lifeJitter = life * (0.6 + Math.random() * 0.8);
        startScale = 0.6 + Math.random() * 0.8;
        endScale = 0.3;
        ay = 280;
        const len = 8 + Math.random() * 14;
        gfx.rect(-1.5, 0, 3, len);
        gfx.fill({ color, alpha: 0.85 });
        break;
      }
      case 'ring': {
        angle = 0;
        particleSpeed = 0;
        lifeJitter = life * (0.7 + (i / count) * 0.5);
        startAlpha = 0.7;
        startScale = 0.2 + i * 0.15;
        endScale = 1.6 + i * 0.25;
        const r = 28;
        gfx.circle(0, 0, r);
        gfx.stroke({ color, width: 2, alpha: 0.8 });
        break;
      }
    }

    container.addChild(gfx);
    particles.push({
      gfx,
      vx: Math.cos(angle) * particleSpeed,
      vy: Math.sin(angle) * particleSpeed,
      ax,
      ay,
      life: lifeJitter,
      age: 0,
      startScale,
      endScale,
      startAlpha,
    });
  }

  // Per-burst rAF loop. Tied to wall-clock, not playback speed, so VFX
  // always finish in the same real time regardless of 1×/2×/4×.
  let lastTime = performance.now();
  let rafId = 0;

  function tick(now: number) {
    const dt = Math.min(64, now - lastTime) / 1000;
    lastTime = now;

    let alive = false;
    for (const p of particles) {
      if (p.age >= p.life) continue;
      p.age += dt * 1000;
      const t = Math.min(1, p.age / p.life);
      p.gfx.x += p.vx * dt;
      p.gfx.y += p.vy * dt;
      p.vx += p.ax * dt;
      p.vy += p.ay * dt;
      const s = p.startScale + (p.endScale - p.startScale) * t;
      p.gfx.scale.set(s);
      p.gfx.alpha = p.startAlpha * (1 - t);
      if (p.age < p.life) alive = true;
    }

    if (alive) {
      rafId = requestAnimationFrame(tick);
    } else {
      // Destroy children + container so we don't accumulate GPU buffers.
      try {
        container.destroy({ children: true });
      } catch {
        // already destroyed (e.g., parent unmounted)
      }
      cancelAnimationFrame(rafId);
    }
  }

  rafId = requestAnimationFrame(tick);
}
